import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  Button,
  useTheme,
  CardContent,
  Avatar,
} from '@mui/material'
import { Link } from 'react-router-dom'
import { 
  Rocket, 
  Science, 
  Engineering, 
  Public,
} from '@mui/icons-material'
import siteConfig from '../config/siteConfig'
import { images } from '../config/images'

const About: React.FC = () => {
  const theme = useTheme()

  const values = [
    {
      icon: <Science sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Innovation',
      description: 'Pushing the boundaries of what\'s possible in rocket propulsion through cutting-edge research and development.',
    },
    {
      icon: <Engineering sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Excellence',
      description: 'Maintaining the highest standards in engineering, testing, and competition performance.',
    },
    {
      icon: <Public sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Collaboration',
      description: 'Building bridges between academia, industry, and the global aerospace community.',
    },
    {
      icon: <Rocket sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Vision',
      description: 'Inspiring the next generation of aerospace engineers and advancing space exploration capabilities.',
    },
  ]

  const teamMembers = [
    {
      name: 'Rodrigo Hernández',
      role: 'Business Development Manager',
      bio: '',
      avatar: '👨‍🚀',
    },
    {
      name: 'Godric Aceves',
      role: 'IT Systems Engineer',
      bio: '',
      avatar: '👨‍🚀',
    },
    {
      name: 'Alberto Ojeda',
      role: 'Team Leader',
      bio: '',
      avatar: '👨‍🚀',
    },
    {
      name: 'Luis Cosio',
      role: 'Research Engineer',
      bio: '',
      avatar: '👨‍🚀',
    },
    {
      name: 'Marian Munguía',
      role: 'Simulation Engineer',
      bio: '',
      avatar: '👨‍🚀',
    },
    {
      name: 'Bruno Alvarado',
      role: 'Hybrid Systems Engineer',
      bio: '',
      avatar: '👨‍🚀',
    },
  ]

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${images.backgrounds.aboutHero}) center/cover`,
          color: 'white',
          py: 12,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '3rem', md: '4.5rem' },
              fontWeight: 800,
              mb: 3,
            }}
          >
            About Us
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.2rem', md: '1.5rem' },
              maxWidth: '800px',
              mx: 'auto',
              opacity: 0.9,
              lineHeight: 1.6,
            }}
          >
            From university laboratory to international competition stages, 
            we're redefining what's possible in rocket propulsion
          </Typography>
        </Container>
      </Box>

      {/* Mission Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontSize: { xs: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 4,
              }}
            >
              Our Mission
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ fontSize: '1.2rem', lineHeight: 1.7, mb: 3 }}
            >
              Propulsion Industries exists to revolutionize rocket propulsion technology through 
              innovative motor systems. We combine cutting-edge engineering with competitive 
              excellence to push the boundaries of what's achievable in aerospace.
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ fontSize: '1.2rem', lineHeight: 1.7, mb: 3 }}
            >
              Our team of passionate engineers and researchers from top Mexican universities works 
              tirelessly to develop solutions that not only win competitions but also contribute 
              to the advancement of space exploration capabilities.
            </Typography>
            <Button
              component={Link}
              to="/projects"
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 2 }}
            >
              View Our Projects
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                textAlign: 'center',
                fontSize: '12rem',
                opacity: 0.8,
              }}
            >
              🚀
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Values Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography
          variant="h2"
          textAlign="center"
          gutterBottom
          sx={{
            fontSize: { xs: '2.5rem', md: '3rem' },
            fontWeight: 700,
            mb: 8,
          }}
        >
          Our Values
        </Typography>
        <Grid container spacing={4}>
          {values.map((value, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 15px 35px rgba(255,0,0,0.2)`,
                  },
                }}
              >
                <Box sx={{ mb: 3 }}>
                  {value.icon}
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {value.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {value.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Team Section */}
      {siteConfig.about.team && (
        <Box sx={{ py: 10 }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              textAlign="center"
              gutterBottom
              sx={{
                fontSize: { xs: '2.5rem', md: '3rem' },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Meet Our Team
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 8, maxWidth: 600, mx: 'auto' }}
            >
              The minds behind Propulsion Industries
            </Typography>
            <Grid container spacing={4}>
              {teamMembers.map((member, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(255,0,0,0.15)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 2,
                            fontSize: '2.5rem',
                            bgcolor: 'transparent',
                          }}
                        >
                          {member.avatar}
                        </Avatar>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                          {member.name}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            mb: 2,
                          }}
                        >
                          {member.role}
                        </Typography>
                      </Box>
                      
                      <Typography
                        variant="body2"
                        paragraph
                        sx={{ lineHeight: 1.6, mb: 3 }}
                      >
                        {member.bio}
                      </Typography>
                      
                      {/*<Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
                        <Button
                          size="small"
                          startIcon={<LinkedIn />}
                          sx={{ minWidth: 'auto', p: 1 }}
                        >
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Email />}
                          sx={{ minWidth: 'auto', p: 1 }}
                        >
                        </Button>
                      </Box>*/}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}
    </Box>
  )
}

export default About
